import React, { useState, useEffect, useCallback } from "react";
import { fmtMonth } from "../utils/helpers";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const CHANNEL_LABELS = { etsy: "Etsy", ebay: "eBay", reverb: "Reverb", amazon: "Amazon", tiktok: "TikTok Shop", shopify: "Shopify" };
const CHANNEL_ORDER = ["etsy", "ebay", "reverb", "amazon", "tiktok", "shopify"];

const currentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const fmtMoney = (v) => `$${parseFloat(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtGrams = (g) => (g >= 1000 ? `${(g / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} kg` : `${Math.round(g * 10) / 10} g`);

// Only the CEO (01) and COO (02) can mark VV's filament paid back to HQ — same gate as salary Mark Paid
const EDITOR_IDS = ["01", "02"];
const sessionUser = () => { try { return JSON.parse(sessionStorage.getItem("nycmfg-user")) || {}; } catch { return {}; } };

const fmtSynced = (iso) => {
  if (!iso) return { label: "never", stale: true };
  const d = new Date(iso.endsWith("Z") ? iso : iso + "Z");
  const hrs = (Date.now() - d.getTime()) / 3600000;
  const label = d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return { label, stale: hrs > 48 };
};

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Month-over-month bar chart: kg of filament per month. Click a bar to open that month.
function UsageChart({ months, selected, onSelect }) {
  const [hover, setHover] = useState(null);
  const W = 720, H = 250, padL = 44, padR = 8, padT = 26, padB = 44;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const maxKg = Math.max(1, ...months.map((m) => m.totalGrams / 1000));
  const step = maxKg <= 5 ? 1 : maxKg <= 20 ? 5 : maxKg <= 50 ? 10 : 25;
  const top = Math.ceil(maxKg / step) * step;
  const ticks = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  const slot = plotW / Math.max(1, months.length);
  const barW = Math.min(44, slot - 10);
  const yOf = (kg) => padT + plotH - (kg / top) * plotH;
  const h = hover != null ? months[hover] : null;

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }} role="img" aria-label="Filament used per month, in kilograms">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={yOf(t)} y2={yOf(t)} stroke="rgba(10,10,15,0.1)" strokeWidth="1" />
            <text x={padL - 8} y={yOf(t) + 4} textAnchor="end" fontSize="11" fill="rgba(10,10,15,0.58)" fontFamily="'JetBrains Mono', monospace">{t}</text>
          </g>
        ))}
        <text x={padL - 8} y={padT - 12} textAnchor="end" fontSize="10" fill="rgba(10,10,15,0.58)" fontFamily="'JetBrains Mono', monospace">kg</text>
        {months.map((m, i) => {
          const kg = m.totalGrams / 1000;
          const cx = padL + slot * i + slot / 2;
          const y = yOf(kg);
          const bh = padT + plotH - y;
          const r = Math.min(4, bh / 2, barW / 2);
          const isSel = m.month === selected;
          const x = cx - barW / 2, base = padT + plotH;
          const path = bh > 0 ? `M${x},${base} V${y + r} Q${x},${y} ${x + r},${y} H${x + barW - r} Q${x + barW},${y} ${x + barW},${y + r} V${base} Z` : "";
          const [yy, mm] = m.month.split("-");
          return (
            <g key={m.month} style={{ cursor: "pointer" }}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onClick={() => onSelect(m.month)}>
              <rect x={cx - slot / 2} y={padT} width={slot} height={plotH + padB} fill="transparent" />
              {path && <path d={path} fill={isSel ? "#0033FF" : "rgba(0,51,255,0.45)"} />}
              <text x={cx} y={base + 16} textAnchor="middle" fontSize="11" fontWeight={isSel ? 700 : 400} fill={isSel ? "#0A0A0F" : "rgba(10,10,15,0.58)"}>
                {MONTH_SHORT[parseInt(mm, 10) - 1]}{mm === "01" || i === 0 ? ` ${yy.slice(2)}` : ""}
              </text>
              {m.paid && <text x={cx} y={base + 32} textAnchor="middle" fontSize="10" fontWeight="700" fill="rgba(10,10,15,0.58)">✓ paid</text>}
            </g>
          );
        })}
        <line x1={padL} x2={W - padR} y1={padT + plotH} y2={padT + plotH} stroke="rgba(10,10,15,0.4)" strokeWidth="1" />
      </svg>
      {h && (
        <div style={{
          position: "absolute", top: 0, pointerEvents: "none",
          left: `${((padL + slot * hover + slot / 2) / W) * 100}%`,
          transform: hover > months.length / 2 ? "translateX(calc(-100% - 12px))" : "translateX(12px)",
          background: "#FFFFFF", border: "2px solid #0A0A0F", padding: "8px 12px", fontSize: 13, whiteSpace: "nowrap", zIndex: 2,
        }}>
          <div style={{ fontWeight: 700, color: "#0A0A0F", marginBottom: 4 }}>{fmtMonth(h.month)}</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F" }}>{fmtGrams(h.totalGrams)}</div>
          <div style={{ color: "rgba(10,10,15,0.58)" }}>{h.totalUnits} units · {fmtMoney(h.cost)}</div>
          <div style={{ color: "rgba(10,10,15,0.58)" }}>{h.paid ? `✓ Paid to HQ (${fmtMoney(h.paid.amount)})` : "Not paid yet"}</div>
        </div>
      )}
    </div>
  );
}

export default function FilamentUsagePage({ store, storeName }) {
  const [month, setMonth] = useState(currentMonthKey());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [costInput, setCostInput] = useState("");
  const [costDirty, setCostDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);

  // Chart: the 12 months ending this month. Reloads after any change on the page.
  const fetchHistory = useCallback(() => {
    fetch(`${API_URL}/api/filament-usage/history?store=${store}&months=12`)
      .then((r) => r.json())
      .then((d) => { if (!d.error) setHistory(d.months || []); })
      .catch(() => {});
  }, [store]);

  const fetchUsage = useCallback(() => {
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/filament-usage?store=${store}&month=${month}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) { setError(d.error); return; }
        setData(d);
        setCostDirty((dirty) => {
          if (!dirty) setCostInput(d.costPerKg ? String(d.costPerKg) : "");
          return dirty;
        });
      })
      .catch(() => setError("Could not load — is the server reachable?"))
      .finally(() => setLoading(false));
  }, [store, month]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);
  useEffect(() => { fetchHistory(); }, [fetchHistory, data]);

  const saveCost = () => {
    fetch(`${API_URL}/api/filament-usage/settings`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ store, costPerKg: parseFloat(costInput) || 0 }),
    })
      .then((r) => r.json())
      .then(() => { setCostDirty(false); fetchUsage(); })
      .catch(() => {});
  };

  const sendToExpenses = (undo) => {
    if (busy) return;
    setBusy(true);
    setError("");
    fetch(`${API_URL}/api/filament-usage/${undo ? "unsend-expense" : "send-to-expenses"}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ store, month }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) { setError(d.error); return; }
        fetchUsage();
      })
      .catch(() => setError("Request failed — is the server reachable?"))
      .finally(() => setBusy(false));
  };

  const markPaid = (undo) => {
    if (busy) return;
    setBusy(true);
    setError("");
    fetch(`${API_URL}/api/filament-usage/${undo ? "unmark-paid" : "mark-paid"}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ store, month, paidBy: sessionUser().name || "" }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) { setError(d.error); return; }
        fetchUsage();
      })
      .catch(() => setError("Request failed — is the server reachable?"))
      .finally(() => setBusy(false));
  };

  const canMarkPaid = EDITOR_IDS.includes(String(sessionUser().empId || ""));
  const paid = data?.paid || null;
  const paidThru = data?.paidThru || "";

  const totalGrams = data?.totalGrams || 0;
  const costPerKg = data?.costPerKg || 0;
  const cost = data?.cost || 0;
  const spools = totalGrams / 1000;
  const channels = data?.channels || {};
  const products = data?.products || [];
  const unmatched = data?.unmatched || [];
  const expense = data?.expense || null;

  const channelKeys = CHANNEL_ORDER.filter((k) => channels[k]);

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>{storeName} — Filament Usage</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Grams of filament in units sold across every channel — review monthly, send to expenses</p>
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <label style={S.label}>Month</label>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ ...S.input, marginBottom: 0, width: 170 }} />
        </div>
        <div>
          <label style={S.label}>Filament Cost ($ / kg)</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input type="number" value={costInput} step="0.01" placeholder="e.g. 20.00"
              onChange={(e) => { setCostInput(e.target.value); setCostDirty(true); }}
              style={{ ...S.input, marginBottom: 0, width: 120, textAlign: "right" }} />
            {costDirty && <button onClick={saveCost} style={{ ...S.saveJobBtn, padding: "8px 14px", fontSize: 13 }}>Save</button>}
          </div>
        </div>
        <button onClick={fetchUsage} disabled={loading} style={{ ...S.addBtn, opacity: loading ? 0.5 : 1 }}>{loading ? "Loading…" : "Refresh"}</button>
      </div>

      {error && <div style={{ marginBottom: 16, padding: "10px 16px", background: "rgba(239,68,68,0.08)", border: "1px solid #ef4444", color: "#ef4444", fontSize: 14, fontWeight: 600 }}>{error}</div>}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div style={S.statCard}><span style={S.statLabel}>Filament Used</span><span style={{ ...S.statValue, color: "#0033FF" }}>{fmtGrams(totalGrams)}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>≈ Spools (1kg)</span><span style={S.statValue}>{spools.toLocaleString(undefined, { maximumFractionDigits: 1 })}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Units Sold</span><span style={S.statValue}>{data?.totalUnits || 0}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Filament Cost</span><span style={{ ...S.statValue, color: costPerKg > 0 ? "#34d399" : "rgba(10,10,15,0.4)" }}>{costPerKg > 0 ? fmtMoney(cost) : "set $/kg"}</span></div>
      </div>

      {/* Month over month */}
      <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 4 }}>Month over Month</h3>
      <p style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>Kilograms of filament in the units sold each month, last 12 months. Click a bar to open that month. "✓ paid" means {storeName} paid HQ back for it.</p>
      <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", padding: "12px 16px", marginBottom: 24 }}>
        {history.length > 0
          ? <UsageChart months={history} selected={month} onSelect={setMonth} />
          : <div style={{ padding: 30, textAlign: "center", fontSize: 14, color: "rgba(10,10,15,0.58)" }}>Loading chart…</div>}
      </div>

      {/* Send to Expenses */}
      <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", padding: "14px 20px", marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        {expense ? (
          <>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#34d399" }}>
              ✓ Sent to Expenses — {fmtMoney(expense.cost)} on {expense.date} <span style={{ color: "rgba(10,10,15,0.58)", fontWeight: 400 }}>({expense.description})</span>
            </span>
            <button onClick={() => sendToExpenses(true)} disabled={busy} style={{ ...S.clearBtn, padding: "6px 14px", fontSize: 13, opacity: busy ? 0.5 : 1 }}>Undo</button>
          </>
        ) : (
          <>
            <span style={{ fontSize: 14, color: "rgba(10,10,15,0.58)" }}>
              Post <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#0A0A0F" }}>{fmtGrams(totalGrams)}</span>
              {costPerKg > 0 && <> ({fmtMoney(cost)})</>} as a <span style={{ fontWeight: 700 }}>Filament</span> expense for {fmtMonth(month)}
            </span>
            <button onClick={() => sendToExpenses(false)} disabled={busy || totalGrams <= 0 || costPerKg <= 0}
              title={costPerKg <= 0 ? "Set a cost per kg first" : totalGrams <= 0 ? "No usage this month" : undefined}
              style={{ ...S.saveJobBtn, padding: "8px 16px", fontSize: 13, opacity: busy || totalGrams <= 0 || costPerKg <= 0 ? 0.5 : 1 }}>
              Send to Expenses
            </button>
          </>
        )}
      </div>

      {/* Mark Paid — VV pays HQ back for the filament it used this month */}
      <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", padding: "14px 20px", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          {paid ? (
            <span style={{ fontSize: 14, fontWeight: 700, color: "#34d399" }}>
              ✓ Paid to HQ — {fmtMoney(paid.amount)} <span style={{ color: "rgba(10,10,15,0.58)", fontWeight: 400 }}>({fmtGrams(paid.grams)} @ {fmtMoney(paid.costPerKg)}/kg · marked by {paid.paidBy} on {paid.paidAt})</span>
            </span>
          ) : (
            <span style={{ fontSize: 14, color: "rgba(10,10,15,0.58)" }}>
              {storeName} owes HQ <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#0A0A0F" }}>{costPerKg > 0 ? fmtMoney(cost) : "—"}</span> for filament used in {fmtMonth(month)}
            </span>
          )}
          {canMarkPaid && (paid ? (
            <button onClick={() => markPaid(true)} disabled={busy} style={{ ...S.clearBtn, padding: "6px 14px", fontSize: 13, opacity: busy ? 0.5 : 1 }}>Undo</button>
          ) : (
            <button onClick={() => markPaid(false)} disabled={busy || costPerKg <= 0}
              style={{ ...S.saveJobBtn, padding: "8px 16px", fontSize: 13, opacity: busy || costPerKg <= 0 ? 0.5 : 1 }}>
              Mark Paid
            </button>
          ))}
        </div>
        <p style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", margin: "8px 0 0" }}>
          {paidThru ? <>Paid thru <span style={{ fontWeight: 700, color: "#0A0A0F" }}>{fmtMonth(paidThru)}</span>. </> : "No month is marked paid yet. "}
          Mark Paid saves the amount at today's numbers. If sales change later, Undo and mark again.
        </p>
      </div>

      {/* Channels */}
      <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>By Channel</h3>
      <div style={{ ...S.tableWrap, overflowX: "auto", marginBottom: 24 }}>
        <table style={{ ...S.table, minWidth: 560 }}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: "24%" }}>Channel</th>
              <th style={{ ...S.th, width: "14%", textAlign: "right" }}>Orders</th>
              <th style={{ ...S.th, width: "14%", textAlign: "right" }}>Units</th>
              <th style={{ ...S.th, width: "20%", textAlign: "right" }}>Filament</th>
              <th style={{ ...S.th, width: "28%", textAlign: "right" }}>Last Order Synced</th>
            </tr>
          </thead>
          <tbody>
            {channelKeys.map((k) => {
              const c = channels[k];
              const sync = fmtSynced(c.lastSynced);
              return (
                <tr key={k}>
                  <td style={{ ...S.td, fontWeight: 600, color: "#0A0A0F" }}>{CHANNEL_LABELS[k] || k}</td>
                  <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", textAlign: "right" }}>{c.orders}</td>
                  <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", textAlign: "right" }}>{c.units}</td>
                  <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", textAlign: "right", color: "#0033FF", fontWeight: 600 }}>{fmtGrams(c.grams)}</td>
                  <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, textAlign: "right", color: sync.stale ? "#f59e0b" : "rgba(10,10,15,0.58)" }}
                    title={sync.stale ? "No recent sync — run a sync on this channel's page before trusting this month's numbers" : undefined}>
                    {sync.stale ? "⚠ " : ""}{sync.label}
                  </td>
                </tr>
              );
            })}
            {channelKeys.length === 0 && (
              <tr><td colSpan={5} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 30 }}>No channel data yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Product breakdown */}
      <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>By Product</h3>
      <div style={{ ...S.tableWrap, overflowX: "auto", marginBottom: 24 }}>
        <table style={{ ...S.table, minWidth: 640 }}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: "34%" }}>Product</th>
              <th style={{ ...S.th, width: "14%" }}>SKU</th>
              <th style={{ ...S.th, width: "13%", textAlign: "right" }}>g / unit</th>
              <th style={{ ...S.th, width: "13%", textAlign: "right" }}>Units</th>
              <th style={{ ...S.th, width: "14%", textAlign: "right" }}>Filament</th>
              <th style={{ ...S.th, width: "12%", textAlign: "right" }}>% of total</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p, i) => (
              <tr key={i}>
                <td style={{ ...S.td, fontWeight: 600, color: "#0A0A0F" }}>{p.name}</td>
                <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{p.sku || "—"}</td>
                <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", textAlign: "right" }}>{p.gramsEach}g</td>
                <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", textAlign: "right" }}>{p.units}</td>
                <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", textAlign: "right", color: "#0033FF", fontWeight: 600 }}>{fmtGrams(p.grams)}</td>
                <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, textAlign: "right", color: "rgba(10,10,15,0.58)" }}>{totalGrams > 0 ? `${((p.grams / totalGrams) * 100).toFixed(1)}%` : "—"}</td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 30 }}>No filament usage recorded for {fmtMonth(month)} — check that products have grams set.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Unmatched */}
      {unmatched.length > 0 && (
        <>
          <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, color: "#f59e0b", marginBottom: 8 }}>⚠ Not Counted ({unmatched.length})</h3>
          <p style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>These sold items were skipped — fix the SKU or set grams on the product, then Refresh.</p>
          <div style={{ ...S.tableWrap, overflowX: "auto", marginBottom: 24 }}>
            <table style={{ ...S.table, minWidth: 640 }}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width: "14%" }}>Channel</th>
                  <th style={{ ...S.th, width: "16%" }}>SKU</th>
                  <th style={{ ...S.th, width: "40%" }}>Item</th>
                  <th style={{ ...S.th, width: "18%" }}>Reason</th>
                  <th style={{ ...S.th, width: "12%", textAlign: "right" }}>Units</th>
                </tr>
              </thead>
              <tbody>
                {unmatched.map((u, i) => (
                  <tr key={i}>
                    <td style={{ ...S.td, color: "#0A0A0F" }}>{CHANNEL_LABELS[u.channel] || u.channel}</td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>{u.sku || "—"}</td>
                    <td style={{ ...S.td, fontSize: 13, color: "rgba(10,10,15,0.58)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 320 }}>{u.title || "—"}</td>
                    <td style={{ ...S.td }}><span style={{ fontSize: 12, fontWeight: 700, color: "#f59e0b", textTransform: "uppercase", letterSpacing: 0.5 }}>{u.reason}</span></td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", textAlign: "right" }}>{u.units}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
